import { useEffect, useRef, useState, type FormEvent } from 'react';
import { _registerDispatcher, type ResolvedDialog } from '../lib/dialog';

/**
 * The single host that renders every confirmDialog / promptDialog /
 * alertDialog call across the app. Mounted once at the App root —
 * see App.tsx. Registers itself as the global dispatcher in
 * lib/dialog.ts so any module can pop a styled dialog without
 * passing a context-bound hook around.
 *
 * Why a singleton instead of a context hook? Imperative replacement
 * for window.confirm is a 1-line swap (`await confirmDialog(...)`).
 * A useDialog() hook would require every caller to be a component
 * and to thread state around. The singleton matches the API the
 * native dialogs already presented.
 *
 * Stacking: we only allow ONE dialog at a time. If a second request
 * arrives while another is open it's queued FIFO. In practice the
 * UI never opens two; the queue is defensive.
 */
export function DialogHost() {
  const [queue, setQueue] = useState<ResolvedDialog[]>([]);
  const current = queue[0] ?? null;

  // Prompt input state — reset whenever the active dialog changes.
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (current?.kind === 'prompt') {
      setValue(current.opts.defaultValue ?? '');
      // Focus the input on the next tick so the browser has rendered
      // the modal first.
      window.setTimeout(() => inputRef.current?.focus(), 30);
    } else {
      setValue('');
    }
  }, [current]);

  // Register / unregister the dispatcher. enqueue is stable across
  // renders because setQueue's functional form doesn't need a fresh
  // closure each render.
  useEffect(() => {
    const enqueue = (dialog: ResolvedDialog) => {
      setQueue((prev) => [...prev, dialog]);
    };
    _registerDispatcher(enqueue);
    return () => {
      _registerDispatcher(null);
    };
  }, []);

  // Escape cancels / dismisses the active dialog.
  useEffect(() => {
    if (!current) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleCancel();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  function pop() {
    setQueue((prev) => prev.slice(1));
  }

  function handleCancel() {
    if (!current) return;
    if (current.kind === 'confirm') current.resolve(false);
    else if (current.kind === 'prompt') current.resolve(null);
    else if (current.kind === 'alert') current.resolve();
    pop();
  }

  function handleConfirm() {
    if (!current) return;
    if (current.kind === 'confirm') current.resolve(true);
    else if (current.kind === 'prompt') current.resolve(value);
    else if (current.kind === 'alert') current.resolve();
    pop();
  }

  function onPromptSubmit(e: FormEvent) {
    e.preventDefault();
    if (current?.kind !== 'prompt') return;
    if (current.opts.requireValue && value.trim().length === 0) return;
    handleConfirm();
  }

  if (!current) return null;

  const isDanger =
    (current.kind === 'confirm' && current.opts.danger) ||
    (current.kind === 'prompt' && current.opts.danger);

  const confirmLabel =
    current.kind === 'alert'
      ? (current.opts.confirmLabel ?? 'OK')
      : (current.opts.confirmLabel ?? (current.kind === 'prompt' ? 'Save' : 'Confirm'));

  const message = current.opts.message ?? null;
  const messageParagraphs = message ? message.split(/\n\n+/) : [];

  return (
    <div className="dialog-overlay" role="dialog" aria-modal="true" onMouseDown={handleCancel}>
      <div
        className={`dialog-card${isDanger ? ' dialog-card--danger' : ''}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2 className="dialog-title">{current.opts.title}</h2>
        {messageParagraphs.length > 0 && (
          <div className="dialog-message">
            {messageParagraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        )}

        {current.kind === 'prompt' && (
          <form onSubmit={onPromptSubmit} className="dialog-form">
            {current.opts.inputLabel && (
              <label className="dialog-input-label">{current.opts.inputLabel}</label>
            )}
            {current.opts.multiline ? (
              <textarea
                ref={(el) => {
                  inputRef.current = el;
                }}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={current.opts.placeholder}
                rows={4}
                className="dialog-input dialog-input--multiline"
              />
            ) : (
              <input
                ref={(el) => {
                  inputRef.current = el;
                }}
                type="text"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={current.opts.placeholder}
                className="dialog-input"
              />
            )}
          </form>
        )}

        <div className="dialog-actions">
          {current.kind !== 'alert' && (
            <button type="button" className="secondary" onClick={handleCancel}>
              {(current.opts as { cancelLabel?: string }).cancelLabel ?? 'Cancel'}
            </button>
          )}
          <button
            type="button"
            className={isDanger ? 'dialog-confirm dialog-confirm--danger' : 'dialog-confirm'}
            disabled={
              current.kind === 'prompt' &&
              current.opts.requireValue === true &&
              value.trim().length === 0
            }
            onClick={handleConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
